'use client';

import { type ForwardRefExoticComponent, Fragment, forwardRef } from 'react';

import { Slottable } from '../slot';
import { VisuallyHidden } from '../visually-hidden/visually-hidden';
import { TagIcon } from './tag-icon';
import { TagLabel } from './tag-label';
import { TagRoot } from './tag-root';
import type { TagPropsInterface } from './types/tag.types';

/**
 * @since 8.6.0
 */
export const Tag = forwardRef<HTMLSpanElement, TagPropsInterface>(
  (props: TagPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      icon,
      size = 'md',
      tone = 'gray',
      children,
      voiceoverAnnounceLabel = 'Status: ',
      ...rest
    } = props;

    // Check if tone is a status and force to show icon when true
    const isStatusTone = ['critical', 'info', 'success', 'warning'].includes(tone);

    return (
      <Tag.Root ref={forwardedRef} asChild={asChild} size={size} tone={tone} {...rest}>
        <Slottable child={children}>
          {(slottable) => (
            <Fragment>
              {(icon || isStatusTone) && size === 'md' ? <Tag.Icon>{icon}</Tag.Icon> : null}
              {isStatusTone && voiceoverAnnounceLabel ? (
                <VisuallyHidden>{voiceoverAnnounceLabel}</VisuallyHidden>
              ) : null}
              <Tag.Label>{slottable}</Tag.Label>
            </Fragment>
          )}
        </Slottable>
      </Tag.Root>
    );
  },
) as ForwardRefExoticComponent<TagPropsInterface> & {
  Root: typeof TagRoot;
  Label: typeof TagLabel;
  Icon: typeof TagIcon;
};

Tag.displayName = 'Tag';

Tag.Root = TagRoot;
Tag.Label = TagLabel;
Tag.Icon = TagIcon;
